import type { ExploreProperty, QuoteRateplan } from '../../widgets/explorer/data'
import type { ExploreWidgetConfig, VisitorFilter } from '../../widgets/explorer/config'
import { useLocaleDetection } from '../../hooks/locale'
import { useLocalizedTranslation } from '../i18n'
import { formatPrice as formatCardPrice } from './card-parts'
import { cardNoDatesLabel, rateUnbookable } from './no-dates-label'
import { isRoundingStrikeWorthShowing, originalTotalOf } from '../rounding-strike-guard'

export interface GridQuotePriceProps {
  property: ExploreProperty
  options: ExploreWidgetConfig
  visitorFilters?: VisitorFilter
  quoteData?: { rateplan: QuoteRateplan; nights: number } | null
  quoteLoading?: boolean
  quoteFailed?: boolean
}

/**
 * The explorer's price display, rendered with the template family's hr-pc-*
 * classes. A deliberate port (not an extraction) of TemplateExplorer's
 * renderPrice: that function's .hfse-* markup is pinned by the #895 skin CSS
 * DOM contract, and #902 retires it — until then the decision tree lives
 * twice, here and there, and the two must stay behaviorally identical.
 * Modes (WP parity): none / base / lowest / quote total|average|per_night,
 * with the calendar-derived per-stay (#2565) and base-rate fallbacks.
 *
 * The __pernight / __taxnote / __notesep spans (and the __note--tax variant on
 * tax-only notes) exist for buildCardSkinCss's priceArrangement/showTaxLine
 * rules (#902) — they wrap existing text without changing it, so the two
 * trees still render identical strings.
 *
 * #1262: every word here reads the explorer's translations group through
 * `_t` (operator value → shipped locale table → en_US default). The en_US
 * output is byte-identical to the hard-coded strings it replaced, so the
 * "identical trees" contract with TemplateExplorer holds for English.
 */
export default function GridQuotePrice({
  property,
  options,
  visitorFilters,
  quoteData,
  quoteLoading,
  quoteFailed,
}: GridQuotePriceProps) {
  // Every price this card prints is in the property's own currency (#1489).
  // Bound once so each call site below reads exactly as it did.
  const formatPrice = (amount: number) => formatCardPrice(amount, property.currency)
  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('explorer', options.translations, detectedLocale, options.language)
  const nightsLabel = (nights: number) =>
    nights === 1 ? _t('price_night_one', 'night') : _t('price_night_other', 'nights')

  const isDateQuery = Boolean(visitorFilters?.checkin && visitorFilters?.checkout)
  const mode = isDateQuery ? options.priceDisplay.withDates : options.priceDisplay.noDates

  if (mode === 'none') return null

  // Toolkit lowest-rate-availability: no bookable nights → label, not the base rate.
  // Blank label = legacy fallback. With full-price quotes ON for a dated search the
  // quote queue is sequential, so reaching a fallback with no quoteData/loading/
  // failed means "still waiting", never "unavailable" — no label while pending.
  // Null too when Central flags the calendar stale or reports open nights
  // (central#3089): a rate nobody has vouched for is not "no dates".
  const unavailableLabel = cardNoDatesLabel(options, property)
  const quotePending = isDateQuery && !!options.fullPrice
  const renderUnavailable = () => (
    <p className="hr-pc-price hr-pc-price--quote hr-pc-price--unavailable">{unavailableLabel}</p>
  )

  if (mode === 'base' || mode === 'lowest') {
    // lowest: the per-range low, then the calendar-derived low. null = Central says
    // nothing is bookable; undefined = this payload lacks the field.
    const lowestCandidate = property.datesPrice?.lowest ?? property.lowestPrice
    if (mode === 'lowest' && unavailableLabel && rateUnbookable(lowestCandidate)) return renderUnavailable()
    const p = mode === 'lowest' ? (lowestCandidate ?? property.price) : property.price
    if (!(p > 0)) return null
    return (
      <p className="hr-pc-price hr-pc-price--quote">
        {mode === 'lowest' ? _t('price_from', 'From') : _t('price_starting_at', 'Starting at')} <b>{formatPrice(p)}</b>
      </p>
    )
  }

  if (quoteLoading) {
    return (
      <p className="hr-pc-price hr-pc-price--quote hr-pc-price--loading">
        <svg width="100" height="14" viewBox="0 0 100 14" aria-hidden="true">
          <defs>
            <linearGradient id={`hr-pc-price-grad-${property.id}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#eee" />
              <stop offset="50%" stopColor="#ddd" />
              <stop offset="100%" stopColor="#eee" />
              <animate attributeName="x1" values="-1;1" dur="1.5s" repeatCount="indefinite" />
              <animate attributeName="x2" values="0;2" dur="1.5s" repeatCount="indefinite" />
            </linearGradient>
          </defs>
          <rect width="100" height="14" rx="3" fill={`url(#hr-pc-price-grad-${property.id})`} />
        </svg>
      </p>
    )
  }

  if (quoteFailed) {
    return <p className="hr-pc-price hr-pc-price--quote hr-pc-price--failed">{_t('price_view_full', 'View Full Price')}</p>
  }

  if (quoteData) {
    const { rateplan, nights } = quoteData
    // #1064 toolkit rounding-strike-guard: only strike when the discount is
    // large enough per night to be real, not Central's sub-dollar rounding
    // reconciliation on a cents-priced rate (common/rounding-strike-guard.ts).
    const hasDiscount = isRoundingStrikeWorthShowing(rateplan.discount, nights)
    const afterTax = options.fullPriceAfterTax === true
    const taxDeduction = afterTax ? 0 : rateplan.totalTaxes
    const taxNote = afterTax ? _t('price_incl_taxes', 'including taxes & fees') : _t('price_excl_taxes', 'excluding taxes')
    const totalPrice = rateplan.total - taxDeduction + Math.abs(rateplan.discount)
    const totalPriceDiscounted = rateplan.total - taxDeduction
    const avgPrice = totalPrice / nights
    const avgPriceDiscounted = totalPriceDiscounted / nights
    const nightsText = `${nights} ${nightsLabel(nights)}`
    // #1691 toolkit original-total: struck pre-discount total beside the total
    // in the modes that print the total unstruck (total mode already strikes it).
    const originalTotal = options.showOriginalTotal === true ? originalTotalOf(totalPriceDiscounted, rateplan.discount, nights) : null
    const struckTotal = originalTotal !== null && <><del className="hr-pc-price__strike" data-original-total>{formatPrice(originalTotal)}</del>{' '}</>

    if (mode === 'total') {
      return (
        <div className="hr-pc-price hr-pc-price--quote">
          <p className="hr-pc-price__line">
            {hasDiscount && <del className="hr-pc-price__strike">{formatPrice(totalPrice)}</del>}
            {' '}<b>{formatPrice(totalPriceDiscounted)}</b> {_t('price_total', 'total')}
          </p>
          <p className="hr-pc-price__note">{nightsText}<span className="hr-pc-price__notesep"> · </span><span className="hr-pc-price__taxnote">{taxNote}</span></p>
        </div>
      )
    }

    if (mode === 'average') {
      return (
        <div className="hr-pc-price hr-pc-price--quote">
          <p className="hr-pc-price__line">
            <span className="hr-pc-price__pernight">
              {hasDiscount && <del className="hr-pc-price__strike">{formatPrice(avgPrice)}</del>}
              {' '}<b>{formatPrice(avgPriceDiscounted)}</b> {_t('price_avg_per_night', 'avg/night')}
            </span>
            <span className="hr-pc-price__sep"> · </span>
            <span className="hr-pc-price__total">{struckTotal}{formatPrice(totalPriceDiscounted)} {_t('price_total', 'total')}</span>
          </p>
          <p className="hr-pc-price__note">{nightsText}<span className="hr-pc-price__notesep"> · </span><span className="hr-pc-price__taxnote">{taxNote}</span></p>
        </div>
      )
    }

    // per_night (default)
    return (
      <div className="hr-pc-price hr-pc-price--quote">
        <p className="hr-pc-price__line">
          <span className="hr-pc-price__pernight">
            {hasDiscount && <del className="hr-pc-price__strike">{formatPrice(avgPrice)}</del>}
            {' '}<b>{formatPrice(avgPriceDiscounted)}</b> {_t('price_per_night', '/ night')}
          </span>
          <span className="hr-pc-price__sep"> · </span>
          <span className="hr-pc-price__total">{struckTotal}{formatPrice(totalPriceDiscounted)} {_t('price_total', 'total')}</span>
        </p>
        <p className="hr-pc-price__note hr-pc-price__note--tax">{taxNote}</p>
      </div>
    )
  }

  // Dates queried but no full-price quote (fullPrice off) — calendar-derived
  // per-stay price (#2565).
  if (isDateQuery && property.datesPrice && (property.datesPrice.total ?? 0) > 0) {
    const dp = property.datesPrice
    const nights = dp.nights
    const avg = dp.average ?? Math.round((dp.total ?? 0) / Math.max(nights, 1))
    const note = <p className="hr-pc-price__note">{nights} {nightsLabel(nights)}<span className="hr-pc-price__notesep"> · </span><span className="hr-pc-price__taxnote">{_t('price_excl_taxes_fees', 'excluding taxes & fees')}</span></p>

    if (mode === 'total') {
      return (
        <div className="hr-pc-price hr-pc-price--quote">
          <p className="hr-pc-price__line"><b>{formatPrice(dp.total ?? 0)}</b> {_t('price_total', 'total')}</p>
          {note}
        </div>
      )
    }

    return (
      <div className="hr-pc-price hr-pc-price--quote">
        <p className="hr-pc-price__line">
          <span className="hr-pc-price__pernight">
            <b>{formatPrice(avg)}</b>{mode === 'average' ? ` ${_t('price_avg_per_night', 'avg/night')}` : ` ${_t('price_per_night', '/ night')}`}
          </span>
          <span className="hr-pc-price__sep"> · </span>
          <span className="hr-pc-price__total">{formatPrice(dp.total ?? 0)} {_t('price_total', 'total')}</span>
        </p>
        {note}
      </div>
    )
  }

  // Dates queried, no quote coming, no per-stay calendar data AND no forward-window
  // rate either: nothing bookable — label rather than the base rate. The
  // forward-window check matters: Recommendations cards are fetched WITHOUT the
  // visitor's dates (so Central never attaches datesPrice) but rendered with the
  // dated filters; an absent datesPrice alone is not an unavailability signal.
  if (isDateQuery && !quotePending && unavailableLabel
    && rateUnbookable(property.lowestPrice) && rateUnbookable(property.averagePrice)) return renderUnavailable()

  // No dates / no per-stay data (base & lowest handled early above).
  if (mode === 'average') {
    if (!quotePending && unavailableLabel && rateUnbookable(property.averagePrice)) return renderUnavailable()
    const avg = property.averagePrice ?? property.price
    if (avg > 0) {
      return <p className="hr-pc-price hr-pc-price--quote"><b>{formatPrice(avg)}</b> {_t('price_avg_per_night', 'avg/night')}</p>
    }
  }

  if (property.price > 0) {
    return <p className="hr-pc-price hr-pc-price--quote">{_t('price_starting_at', 'Starting at')} <b>{formatPrice(property.price)}</b></p>
  }

  return null
}
